import { format } from 'date-fns';
import { 
  type ConnectResponse, 
  type CreateContactResponse,
  type SubmitApplicationResponse,
  type ContactCenterAvailabilityResponse,
  type ScheduleAppointmentResponse,
  type ConnectCallResponse,
  type AvailabilityResponse,
  type ConnectKbaResponse,
  type VerifyKbaResponse,
  type ProspectVerifyResponse,
  type StartAutoEchelonResponse,
  type GetOpportunityStatusResponse,
  type ApiStatus 
} from '../types/api';
import type { FormData } from '../types/form';
import type { ILeadsService } from './leads';
import { SimulationSettingEnum } from '../types/simulation';
import { simulationService } from './simulation';
import { logger } from '../utils/logger';

export class MockLeadsService implements ILeadsService {
  private mockDelay = 800;
  private mockSpinwheelId = 'mock-spinwheel-123';
  private mockHubspotRecordId = '101352502280';

  private async delay(): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, this.mockDelay));
  }

  private createSuccessResponse(message: string = 'Success'): ConnectResponse {
    const status: ApiStatus = {
      code: 200,
      message
    };
    return {
      status,
      spinwheelId: this.mockSpinwheelId,
      hubspotRecordId: this.mockHubspotRecordId
    };
  }

  private createErrorResponse(code: number, message: string): ApiStatus {
    return { code, message };
  }

  async connectBySms(
    phoneNumber: string, 
    email: string, 
    dateOfBirth: string,
    loanAmount: number | undefined,
    loanPurpose: string | undefined,
    employmentStatus: string | undefined,
    employmentFrequency: string | undefined,
    educationLevel: string | undefined,
    annualIncome: number | undefined,
    propertyStatus: string | undefined,
    ipAddress: string,
    marketingParams: Record<string, string>
  ): Promise<ConnectResponse> {
    logger.info('Mock: Connecting by SMS', {
      phoneNumber,
      email,
      dateOfBirth,
      loanAmount,
      loanPurpose,
      employmentStatus,
      employmentFrequency,
      educationLevel,
      annualIncome,
      propertyStatus,
      ipAddress
    }, 'MockLeadsService');

    await this.delay();

    const settings = simulationService.getSettings();
    switch (settings[SimulationSettingEnum.SMS_CONNECTION]) {
      case 'user_exists':
        throw new Error('User already exists in the system');
      case 'invalid_phone':
        throw new Error('Invalid phone number format');
      case 'service_unavailable':
        throw new Error('Service temporarily unavailable');
      case 'error_500':
        throw { status: this.createErrorResponse(500, 'Internal server error') };
      default:
        const response = this.createSuccessResponse('SMS code sent successfully');
        logger.info('Mock: SMS connection successful', response, 'MockLeadsService');
        return response;
    }
  }

  async verifyCode(
    code: string,
    phoneNumber: string,
    spinwheelId: string,
    email: string,
    hubspotRecordId: string
  ): Promise<ConnectResponse> {
    logger.info('Mock: Verifying code', {
      code,
      phoneNumber,
      spinwheelId,
      email,
      hubspotRecordId
    }, 'MockLeadsService');

    await this.delay();

    const settings = simulationService.getSettings();
    switch (settings[SimulationSettingEnum.CODE_VERIFICATION]) {
      case 'invalid_code':
        throw new Error('Invalid verification code');
      case 'expired_code':
        throw new Error('Verification code has expired');
      case 'too_many_attempts':
        throw new Error('Too many verification attempts');
      case 'error_500':
        throw { status: this.createErrorResponse(500, 'Internal server error') };
      default:
        const response = this.createSuccessResponse('Code verified successfully');
        logger.info('Mock: Code verification successful', response, 'MockLeadsService');
        return response;
    }
  }

  async createContact(
    spinwheelId: string,
    email: string,
    hubspotRecordId: string
  ): Promise<CreateContactResponse> {
    logger.info('Mock: Creating contact', {
      spinwheelId,
      email,
      hubspotRecordId
    }, 'MockLeadsService');

    await this.delay();

    const settings = simulationService.getSettings();
    switch (settings[SimulationSettingEnum.CONTACT_CREATION]) {
      case 'qualified':
        const qualifiedResponse = {
          spinwheelStatus: 'QUALIFIED',
          firstName: 'Michelle',
          state: 'accepted',
          status: this.createErrorResponse(202, 'Lead accepted'),
          debtAmount: 20000,
          debtType: 'debt'
        } as const;
        logger.info('Mock: Contact creation successful (Qualified)', qualifiedResponse, 'MockLeadsService');
        return qualifiedResponse;

      case 'qualified_tier2':
        const qualifiedTier2Response = {
          spinwheelStatus: 'QUALIFIED',
          firstName: 'Michelle',
          state: 'accepted',
          status: this.createErrorResponse(202, 'Lead accepted'),
          debtAmount: 20000,
          debtType: 'debt2'
        } as const;
        logger.info('Mock: Contact creation successful (Qualified Tier 2)', qualifiedTier2Response, 'MockLeadsService');
        return qualifiedTier2Response;

      case 'rejected':
        const rejectedResponse = {
          spinwheelStatus: 'UNQUALIFIED',
          firstName: 'Jeffrey',
          state: 'rejected',
          status: this.createErrorResponse(422, 'Lead rejected'),
          debtAmount: 10000,
          debtType: 'none'
        } as const;
        logger.info('Mock: Contact creation rejected', rejectedResponse, 'MockLeadsService');
        return rejectedResponse;

      case 'error_400':
        throw { status: this.createErrorResponse(400, 'Bad request') };

      case 'error_500':
        throw { status: this.createErrorResponse(500, 'Internal server error') };

      case 'error_503':
        throw { status: this.createErrorResponse(503, 'Service unavailable') };

      case 'error':
        throw new Error('Failed to create contact');

      default:
        const defaultResponse = {
          spinwheelStatus: 'QUALIFIED',
          firstName: 'Michelle',
          state: 'accepted',
          status: this.createErrorResponse(202, 'Lead accepted'),
          debtAmount: 20000,
          debtType: 'debt'
        } as const;
        logger.info('Mock: Contact creation successful (Default)', defaultResponse, 'MockLeadsService');
        return defaultResponse;
    }
  }

  async submitApplication(
    email: string,
    ssnLast4: string,
    loanAmount: number | undefined,
    loanPurpose: string | undefined,
    employmentStatus: string | undefined,
    employmentFrequency: string | undefined,
    educationLevel: string | undefined,
    annualIncome: number | undefined,
    propertyStatus: string | undefined,
    ipAddress: string,
    hubspotRecordId?: string
  ): Promise<SubmitApplicationResponse> {
    logger.info('Mock: Submitting application', {
      email,
      hubspotRecordId,
      ssnLast4: '****',
      loanAmount,
      loanPurpose,
      employmentStatus,
      employmentFrequency,
      educationLevel,
      annualIncome,
      propertyStatus,
      ipAddress
    }, 'MockLeadsService');

    await this.delay();

    const settings = simulationService.getSettings();
    switch (settings[SimulationSettingEnum.APPLICATION_SUBMISSION]) {
      case 'rejected':
        throw new Error('Application rejected');
      case 'system_error':
        throw new Error('System error occurred');
      default:
        const response = {
          status: this.createErrorResponse(200, 'Success'),
          storeUuid: '4e384b88-57de-47d0-94cc-01f6c18fc333',
          offerId: '1bd9e444-4ff1-4354-accb-0a30b1929217',
          offerCount: 0
        };
        logger.info('Mock: Application submission successful', response, 'MockLeadsService');
        return response;
    }
  }

  async getContactCenterAvailability(): Promise<ContactCenterAvailabilityResponse> {
    logger.info('Mock: Checking contact center availability', null, 'MockLeadsService');
    
    await this.delay();

    const settings = simulationService.getSettings();
    switch (settings[SimulationSettingEnum.CONTACT_CENTER]) {
      case 'unavailable':
        const unavailableResponse = {
          status: this.createErrorResponse(200, 'OK'),
          capacity: 0
        };
        logger.info('Mock: Contact center unavailable', unavailableResponse, 'MockLeadsService');
        return unavailableResponse;
      case 'partial':
        const capacity = Math.floor(Math.random() * 2) + 1; // 1-2 capacity
        const partialResponse = {
          status: this.createErrorResponse(200, 'OK'),
          capacity
        };
        logger.info('Mock: Contact center partial availability', partialResponse, 'MockLeadsService');
        return partialResponse;
      default:
        const fullCapacity = Math.floor(Math.random() * 58) + 3; // 3-60 capacity
        const response = {
          status: this.createErrorResponse(200, 'OK'),
          capacity: fullCapacity
        };
        logger.info('Mock: Contact center full availability', response, 'MockLeadsService');
        return response;
    }
  }

  async scheduleAppointment(hubspotRecordId: string, meetingCallTime: string): Promise<ScheduleAppointmentResponse> {
    logger.info('Mock: Scheduling appointment', {
      hubspotRecordId,
      meetingCallTime
    }, 'MockLeadsService');

    await this.delay();

    const response = {
      status: this.createErrorResponse(200, 'Call scheduled successfully')
    };

    logger.info('Mock: Appointment scheduling successful', response, 'MockLeadsService');
    return response;
  }

  async connectCall(hubspotRecordId: string): Promise<ConnectCallResponse> {
    logger.info('Mock: Connecting call', {
      hubspotRecordId
    }, 'MockLeadsService');

    await this.delay();

    const settings = simulationService.getSettings();
    switch (settings[SimulationSettingEnum.CALL_CONNECTION]) {
      case 'invalid_record':
        return {
          status: this.createErrorResponse(400, 'Failed to connect call')
        };
      case 'server_error':
        return {
          status: this.createErrorResponse(500, 'Internal server error')
        };
      default:
        const response = {
          status: this.createErrorResponse(200, 'Call connected'),
          from: '+15551234567'
        };
        logger.info('Mock: Call connection successful', response, 'MockLeadsService');
        return response;
    }
  }

  async createContactExperian(
    email: string,
    fullSsn: string,
    firstName: string,
    lastName: string,
    street: string,
    city: string,
    state: string,
    zip: string,
    hubspotRecordId: string
  ): Promise<CreateContactResponse> {
    logger.info('Mock: Creating contact with Experian', {
      email,
      fullSsn: '*****',
      firstName,
      lastName,
      street,
      city,
      state,
      zip,
      hubspotRecordId
    }, 'MockLeadsService');

    await this.delay();

    const settings = simulationService.getSettings();
    switch (settings[SimulationSettingEnum.EXPERIAN_VERIFICATION]) {
      case 'qualified':
        const qualifiedResponse = {
          spinwheelStatus: 'QUALIFIED',
          firstName,
          state: 'accepted',
          status: this.createErrorResponse(202, 'Lead accepted'),
          debtAmount: 20000,
          debtType: 'debt'
        } as const;
        logger.info('Mock: Experian verification successful (Qualified)', qualifiedResponse, 'MockLeadsService');
        return qualifiedResponse;

      case 'qualified_tier2':
        const qualifiedTier2Response = {
          spinwheelStatus: 'QUALIFIED',
          firstName,
          state: 'accepted',
          status: this.createErrorResponse(202, 'Lead accepted'),
          debtAmount: 20000,
          debtType: 'debt2'
        } as const;
        logger.info('Mock: Experian verification successful (Qualified Tier 2)', qualifiedTier2Response, 'MockLeadsService');
        return qualifiedTier2Response;

      case 'rejected':
        const rejectedResponse = {
          spinwheelStatus: 'UNQUALIFIED',
          firstName,
          state: 'rejected',
          status: this.createErrorResponse(422, 'Lead rejected'),
          debtAmount: 10000,
          debtType: 'none'
        } as const;
        logger.info('Mock: Experian verification rejected', rejectedResponse, 'MockLeadsService');
        return rejectedResponse;

      case 'validation_error':
        throw { status: this.createErrorResponse(400, 'email is required') };

      case 'error_500':
        throw { status: this.createErrorResponse(500, 'Internal server error') };

      case 'error_503':
        throw { status: this.createErrorResponse(503, 'Service unavailable') };

      default: {
        const defaultResponse = {
          spinwheelStatus: 'QUALIFIED',
          firstName,
          state: 'accepted',
          status: this.createErrorResponse(202, 'Lead accepted'),
          debtAmount: 20000,
          debtType: 'debt'
        } as const;
        logger.info('Mock: Experian verification successful (Default)', defaultResponse, 'MockLeadsService');
        return defaultResponse;
      }
    }
  }

  async getAvailability(date: string): Promise<AvailabilityResponse> {
    // Ensure date is in yyyy-MM-dd format
    const formattedDate = this.formatDate(date);
    
    logger.info('Mock: Getting availability', {
      date: formattedDate
    }, 'MockLeadsService');

    await this.delay();

    // Generate realistic mock data for availability
    const mockData = {
      "05:00": {
        "booked": 7,
        "capacity": 11,
        "isAvailable": true,
        "slotsAvailable": 4
      },
      "05:30": {
        "booked": 6,
        "capacity": 11,
        "isAvailable": true,
        "slotsAvailable": 5
      },
      "06:00": {
        "booked": 13,
        "capacity": 33,
        "isAvailable": true,
        "slotsAvailable": 20
      },
      "06:30": {
        "booked": 19,
        "capacity": 33,
        "isAvailable": true,
        "slotsAvailable": 14
      },
      "07:00": {
        "booked": 18,
        "capacity": 61,
        "isAvailable": true,
        "slotsAvailable": 43
      },
      "07:30": {
        "booked": 23,
        "capacity": 61,
        "isAvailable": true,
        "slotsAvailable": 38
      },
      "08:00": {
        "booked": 27,
        "capacity": 84,
        "isAvailable": true,
        "slotsAvailable": 57
      },
      "08:30": {
        "booked": 24,
        "capacity": 84,
        "isAvailable": true,
        "slotsAvailable": 60
      },
      "09:00": {
        "booked": 71,
        "capacity": 91,
        "isAvailable": true,
        "slotsAvailable": 20
      },
      "09:30": {
        "booked": 52,
        "capacity": 91,
        "isAvailable": true,
        "slotsAvailable": 39
      },
      "10:00": {
        "booked": 44,
        "capacity": 91,
        "isAvailable": true,
        "slotsAvailable": 47
      },
      "10:30": {
        "booked": 22,
        "capacity": 91,
        "isAvailable": true,
        "slotsAvailable": 69
      },
      "11:00": {
        "booked": 31,
        "capacity": 84,
        "isAvailable": true,
        "slotsAvailable": 53
      },
      "11:30": {
        "booked": 22,
        "capacity": 84,
        "isAvailable": true,
        "slotsAvailable": 62
      },
      "12:00": {
        "booked": 22,
        "capacity": 84,
        "isAvailable": true,
        "slotsAvailable": 62
      },
      "12:30": {
        "booked": 15,
        "capacity": 84,
        "isAvailable": true,
        "slotsAvailable": 69
      },
      "13:00": {
        "booked": 20,
        "capacity": 87,
        "isAvailable": true,
        "slotsAvailable": 67
      },
      "13:30": {
        "booked": 16,
        "capacity": 87,
        "isAvailable": true,
        "slotsAvailable": 71
      },
      "14:00": {
        "booked": 19,
        "capacity": 91,
        "isAvailable": true,
        "slotsAvailable": 72
      },
      "14:30": {
        "booked": 19,
        "capacity": 91,
        "isAvailable": true,
        "slotsAvailable": 72
      },
      "15:00": {
        "booked": 7,
        "capacity": 96,
        "isAvailable": true,
        "slotsAvailable": 89
      },
      "15:30": {
        "booked": 14,
        "capacity": 96,
        "isAvailable": true,
        "slotsAvailable": 82
      },
      "16:00": {
        "booked": 10,
        "capacity": 48,
        "isAvailable": true,
        "slotsAvailable": 38
      },
      "16:30": {
        "booked": 15,
        "capacity": 48,
        "isAvailable": true,
        "slotsAvailable": 33
      },
      "17:00": {
        "booked": 4,
        "capacity": 19,
        "isAvailable": true,
        "slotsAvailable": 15
      },
      "17:30": {
        "booked": 2,
        "capacity": 19,
        "isAvailable": true,
        "slotsAvailable": 17
      },
      "18:00": {
        "booked": 1,
        "capacity": 7,
        "isAvailable": true,
        "slotsAvailable": 6
      },
      "18:30": {
        "booked": 1,
        "capacity": 7,
        "isAvailable": true,
        "slotsAvailable": 6
      },
      "19:00": {
        "booked": 0,
        "capacity": 2,
        "isAvailable": true,
        "slotsAvailable": 2
      },
      "19:30": {
        "booked": 0,
        "capacity": 2,
        "isAvailable": true,
        "slotsAvailable": 2
      },
      "20:00": {
        "booked": 0,
        "capacity": 0,
        "isAvailable": false,
        "slotsAvailable": 0
      }
    };

    const response = {
      status: this.createErrorResponse(200, 'Availability fetched successfully'),
      totalCalls: 544,
      data: mockData
    };

    logger.info('Mock: Availability fetched successfully', response, 'MockLeadsService');
    return response;
  }

  private formatDate(dateInput: string | Date): string {
    try {
      // If it's already a string and matches yyyy-MM-dd format, return as-is
      if (typeof dateInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
        return dateInput;
      }
      
      // Convert to Date object and format
      const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        throw new Error('Invalid date provided');
      }
      
      return format(date, 'yyyy-MM-dd');
    } catch (error) {
      throw new Error(`Invalid date format: ${dateInput}. Expected yyyy-MM-dd format.`);
    }
  }

  async createManualLead(
    email: string,
    firstName: string,
    lastName: string,
    street: string,
    city: string,
    state: string,
    zip: string,
    offerCode?: string,
    phone?: string,
    dateOfBirth?: string
  ): Promise<CreateContactResponse> {
    logger.info('Mock: Creating manual lead', {
      email,
      firstName,
      lastName,
      street,
      city,
      state,
      zip,
      offerCode,
    }, 'MockLeadsService');

    await this.delay();

    const settings = simulationService.getSettings();
    switch (settings[SimulationSettingEnum.MANUAL_LEAD]) {
      case 'qualified': {
        const response = {
          spinwheelStatus: 'QUALIFIED',
          firstName,
          state: 'accepted',
          status: this.createErrorResponse(200, 'Found user and updated prospect.'),
          debtAmount: 25000,
          debtType: 'debt'
        } as const;
        logger.info('Mock: Manual lead – QUALIFIED', response, 'MockLeadsService');
        return response;
      }

      case 'unqualified': {
        const response = {
          spinwheelStatus: 'UNQUALIFIED',
          firstName,
          state: 'rejected',
          status: this.createErrorResponse(200, 'Found user and updated prospect.'),
          debtAmount: 8000,
          debtType: 'none'
        } as const;
        logger.info('Mock: Manual lead – UNQUALIFIED (debt ≤ 12k)', response, 'MockLeadsService');
        return response;
      }

      case 'not_found':
        throw { status: this.createErrorResponse(404, 'User not found in hubspot, enter correct information') };

      case 'validation_error':
        throw { status: this.createErrorResponse(400, 'addressInfo.zip is required') };

      default: {
        const response = {
          spinwheelStatus: 'UNQUALIFIED',
          firstName,
          state: 'rejected',
          status: this.createErrorResponse(200, 'Found user and updated prospect.'),
          debtAmount: 0,
          debtType: 'none'
        } as const;
        logger.info('Mock: Manual lead – UNQUALIFIED (default)', response, 'MockLeadsService');
        return response;
      }
    }
  }

  async prospectVerify(
    email: string,
    firstName: string,
    lastName: string,
    street: string,
    city: string,
    state: string,
    zip: string,
    offerCode: string,
    phone?: string,
    dateOfBirth?: string
  ): Promise<ProspectVerifyResponse> {
    logger.info('Mock: Prospect verify', {
      email, firstName, lastName, street, city, state, zip
    }, 'MockLeadsService');

    await this.delay();

    const settings = simulationService.getSettings();
    switch (settings[SimulationSettingEnum.PROSPECT_VERIFY]) {
      case 'unqualified': {
        const response: ProspectVerifyResponse = {
          status: { code: 200, message: 'Identity verified successfully.' },
          prospectStatus: 'UNQUALIFIED',
          debtAmount: 8000,
          firstName,
          verifiedVia: 'zip'
        };
        logger.info('Mock: Prospect verify – UNQUALIFIED (debt ≤ 12k)', response, 'MockLeadsService');
        return response;
      }

      case 'validation_error':
        throw { status: this.createErrorResponse(400, 'offerCode is required and must be alphanumeric') };

      case 'error_500':
        throw { status: this.createErrorResponse(500, 'An error occurred') };

      default: {
        const response: ProspectVerifyResponse = {
          status: { code: 200, message: 'Identity verified successfully.' },
          prospectStatus: 'QUALIFIED',
          debtAmount: 35000,
          firstName,
          verifiedVia: 'zip'
        };
        logger.info('Mock: Prospect verify – QUALIFIED', response, 'MockLeadsService');
        return response;
      }
    }
  }

  async connectKba(
    email: string,
    phone: string,
    firstName: string,
    lastName: string,
    dateOfBirth: string,
    ssn: string,
    address: { street: string; city: string; state: string; zipCode: string },
    ipAddress?: string,
    marketingParams?: Record<string, string>
  ): Promise<ConnectKbaResponse> {
    logger.info('Mock: Connecting KBA', {
      email,
      phone,
      firstName,
      lastName,
      dateOfBirth,
      ssn: '*****',
      address,
      ipAddress
    }, 'MockLeadsService');

    await this.delay();

    const settings = simulationService.getSettings();
    if (settings[SimulationSettingEnum.KBA_CONNECT] === 'error_500') {
      throw { status: this.createErrorResponse(500, 'Internal server error') };
    }

    const response: ConnectKbaResponse = {
      status: this.createErrorResponse(200, 'KBA questions retrieved'),
      spinwheelId: this.mockSpinwheelId,
      hubspotRecordId: this.mockHubspotRecordId,
      connectionStatus: 'IN_PROGRESS',
      kbaQuestions: [
        {
          id: 'q1',
          question: 'Which of the following streets have you lived on?',
          options: [
            { id: 'a', text: 'Maple Avenue' },
            { id: 'b', text: 'Oak Street' },
            { id: 'c', text: 'Pine Road' },
            { id: 'd', text: 'None of the above' }
          ]
        },
        {
          id: 'q2',
          question: 'Which of the following vehicles have you owned?',
          options: [
            { id: 'a', text: 'Honda Civic' },
            { id: 'b', text: 'Toyota Camry' },
            { id: 'c', text: 'Ford F-150' },
            { id: 'd', text: 'None of the above' }
          ]
        },
        {
          id: 'q3',
          question: 'Which of the following lenders have you had a mortgage with?',
          options: [
            { id: 'a', text: 'Wells Fargo' },
            { id: 'b', text: 'Chase Bank' },
            { id: 'c', text: 'Bank of America' },
            { id: 'd', text: 'None of the above' }
          ]
        }
      ]
    };

    logger.info('Mock: KBA connect successful', response, 'MockLeadsService');
    return response;
  }

  async verifyKba(
    spinwheelId: string,
    hubspotRecordId: string,
    answers: Record<string, string>,
    email: string
  ): Promise<VerifyKbaResponse> {
    logger.info('Mock: Verifying KBA answers', {
      spinwheelId,
      hubspotRecordId,
      answers,
      email
    }, 'MockLeadsService');

    await this.delay();

    const settings = simulationService.getSettings();
    if (settings[SimulationSettingEnum.KBA_VERIFY] === 'error_500') {
      throw { status: this.createErrorResponse(500, 'Internal server error') };
    }

    if (settings[SimulationSettingEnum.KBA_VERIFY] === 'in_progress') {
      const response: VerifyKbaResponse = {
        status: this.createErrorResponse(200, 'KBA in progress'),
        connectionStatus: 'IN_PROGRESS',
        kbaQuestions: [
          {
            id: 'q1',
            question: 'Which of the following addresses have you lived at?',
            options: [
              { id: 'a1', text: '123 Main St' },
              { id: 'a2', text: '456 Oak Ave' },
              { id: 'a3', text: '789 Pine Rd' },
              { id: 'a4', text: 'None of the above' }
            ]
          },
          {
            id: 'q2',
            question: 'Which of the following vehicles have you owned?',
            options: [
              { id: 'b1', text: '2018 Toyota Camry' },
              { id: 'b2', text: '2015 Honda Civic' },
              { id: 'b3', text: '2020 Ford F-150' },
              { id: 'b4', text: 'None of the above' }
            ]
          }
        ]
      };
      logger.info('Mock: KBA verification in progress (wrong answers)', response, 'MockLeadsService');
      return response;
    }

    const response: VerifyKbaResponse = {
      status: this.createErrorResponse(200, 'KBA verified'),
      connectionStatus: 'SUCCESS'
    };

    logger.info('Mock: KBA verification successful', response, 'MockLeadsService');
    return response;
  }

  // Track success page variant shown to customer
  async successPage(hubspotRecordId: string, variantId: string) {
    logger.info('Mock: Success page variant tracking', { 
      hubspotRecordId, 
      availability: variantId  // Backend field name - variantId includes availability prefix
    }, 'MockLeadsService');
    await this.delay();
    return { status: this.createErrorResponse(200, 'Tracked') } as any;
  }

  async getPartnerPreload(_partnerId: string) {
    await this.delay();
    return {
      loanAmount: 15000,
      loanPurpose: 'debt_consolidation' as const,
      propertyStatus: 'rent' as const,
      employmentStatus: 'employed_full_time' as const,
      employmentFrequency: 'biweekly' as const,
      annualIncome: 80000
    };
  }

  async startAutoEchelon(hubspotRecordId: string): Promise<StartAutoEchelonResponse> {
    logger.info('Mock: Starting auto-echelon', { hubspotRecordId }, 'MockLeadsService');
    await this.delay();
    return {
      status: { code: 200, message: 'Auto-echelon started' },
      opportunityId: 'mock-opportunity-123',
      dealId: 'mock-deal-123'
    };
  }

  async getOpportunityStatus(hubspotRecordId: string, opportunityId: string): Promise<GetOpportunityStatusResponse> {
    logger.info('Mock: Getting opportunity status', { hubspotRecordId, opportunityId }, 'MockLeadsService');
    await this.delay();
    return {
      status: { code: 200, message: 'Opportunity found' },
      aeClientLink: 'https://mock-echelon.symplelending.com/redirect',
      completed: true,
      succeeded: true
    };
  }
}